import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Stack,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { renderIcon } from 'src/constants/icons';
import { Notification } from 'src/types/Notifications';

interface NotificationFiltersProps {
  notifications: Notification[];
  filterState: {
    activeType: string;
    activeSource: string;
    activeReadStatus: string;
    activeNotificationType: string;
    filtersExpanded: boolean;
  };
  filterCounts: {
    readCounts: { total: number; read: number; unread: number };
    typeReadCounts: Record<
      string,
      { total: number; read: number; unread: number }
    >;
    sourceReadCounts: Record<
      string,
      { total: number; read: number; unread: number }
    >;
    notificationTypeCounts: Record<
      string,
      { total: number; read: number; unread: number }
    >;
  };
  onReadStatusChange: (status: string) => void;
  onSourceChange: (source: string) => void;
  onTypeChange: (type: string) => void;
  onNotificationTypeChange: (notificationType: string) => void;
  onToggleFiltersExpanded: () => void;
}

export const NotificationFilters: React.FC<NotificationFiltersProps> = ({
  _notifications,
  filterState,
  filterCounts,
  onReadStatusChange,
  onSourceChange,
  onTypeChange,
  onNotificationTypeChange,
  onToggleFiltersExpanded,
}) => {
  const {
    activeType,
    activeSource,
    activeReadStatus,
    activeNotificationType,
    filtersExpanded,
  } = filterState;
  const {
    readCounts,
    typeReadCounts,
    sourceReadCounts,
    notificationTypeCounts,
  } = filterCounts;

  // Filter options
  const filterTypes = [
    'all',
    'new_email',
    'new_sms',
    'new_fax',
    'new_documents',
    'new_file_owner',
    'new_status_note',
    'recert',
  ];

  const filterAITypes = ['ai_all', 'ai_event', 'ai_action_required'];

  const filterSources = ['all', 'file_owner', 'watching', 'property', 'system'];
  const filterNotificationTypes = [
    'all',
    'single_certification',
    'bulk_certifications',
  ];
  const filterReadStatuses = ['all', 'read', 'unread'];

  // Icons and labels
  const typeIcons = {
    all: renderIcon('filter', 'all'),
    new_email: renderIcon('notification', 'email', {
      style: { color: '#2e7d32' },
    }),
    new_documents: renderIcon('notification', 'documents', {
      style: { color: '#2e7d32' },
    }),
    new_file_owner: renderIcon('notification', 'fileOwner', {
      style: { color: '#2e7d32' },
    }),
    new_sms: renderIcon('notification', 'sms', { style: { color: '#2e7d32' } }),
    new_fax: renderIcon('notification', 'fax', { style: { color: '#2e7d32' } }),
    new_status_note: renderIcon('notification', 'statusNote', {
      style: { color: '#2e7d32' },
    }),
    recert: renderIcon('filter', 'recert', {
      style: { color: '#2e7d32' },
    }),
  };

  const aiTypeIcons = {
    ai_all: renderIcon('filter', 'all', { style: { color: '#2e7d32' } }),
    ai_event: renderIcon('notification', 'info', {
      style: { color: '#1565c0' },
    }),
    ai_action_required: renderIcon('notification', 'bolt', {
      style: { color: '#ed6c02' },
    }),
  };

  const typeLabels = {
    all: 'All Event Types',
    new_email: 'Email',
    new_documents: 'Document',
    new_file_owner: 'File Owner',
    new_sms: 'SMS',
    new_fax: 'Fax',
    new_status_note: 'Status Note',
    recert: 'Recertification',
  };

  const aiTypeLabels = {
    ai_all: 'AI Notifications',
    ai_event: 'AI Event',
    ai_action_required: 'Action Required',
  };

  const sourceIcons = {
    all: renderIcon('filter', 'all', { color: 'primary' }),
    file_owner: renderIcon('header', 'fileOwner', {
      style: { color: '#1976d2' },
    }),
    watching: renderIcon('notification', 'watching', {
      style: { color: '#1976d2' },
    }),
    property: renderIcon('notification', 'property', {
      style: { color: '#1976d2' },
    }),
    system: renderIcon('notification', 'system', {
      style: { color: '#1976d2' },
    }),
  };

  const sourceLabels = {
    all: 'All Sources',
    file_owner: 'File Owner',
    watching: 'Watching',
    property: 'Property',
    system: 'System',
  };

  const notificationTypeIcons = {
    all: renderIcon('filter', 'all', { color: 'primary' }),
    single_certification: renderIcon('filter', 'single', {
      sx: { color: 'text.primary' },
    }),
    bulk_certifications: renderIcon('filter', 'bulk', {
      sx: { color: 'text.primary' },
    }),
  };

  const notificationTypeLabels = {
    all: 'All Types',
    single_certification: 'Single Event',
    bulk_certifications: 'Bulk Events',
  };

  const readStatusIcons = {
    all: renderIcon('filter', 'all'),
    read: renderIcon('filter', 'read', { color: 'success' }),
    unread: renderIcon('filter', 'unread', { color: 'primary' }),
  };

  const readStatusLabels = {
    all: 'All',
    read: 'Read',
    unread: 'Unread',
  };

  // Handler wrappers that call onFilterChange
  const handleReadStatusChangeWithCallback = (status: string): void => {
    onReadStatusChange(status);
  };

  const handleSourceChangeWithCallback = (source: string): void => {
    onSourceChange(source);
  };

  const handleTypeChangeWithCallback = (type: string): void => {
    onTypeChange(type);
  };

  const handleNotificationTypeChangeWithCallback = (
    notificationType: string
  ): void => {
    onNotificationTypeChange(notificationType);
  };

  return (
    <Card sx={{ mb: 2 }} data-tour="filters-card">
      <CardHeader
        data-tour="filters-expand"
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onToggleFiltersExpanded} sx={{ p: 0.5 }}>
              {renderIcon('action', 'filterList', {
                style: { color: '#1976d2' },
              })}
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
                minWidth: 0,
              }}
              data-tour="read-status-filters"
            >
              <Stack direction="row" spacing={1}>
                {filterReadStatuses.map((status) => {
                  const counts = readCounts;
                  return (
                    <Chip
                      key={status}
                      icon={readStatusIcons[status]}
                      label={
                        status === 'unread' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {readStatusLabels[status]} (
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: counts.unread > 0 ? 700 : 400,
                                fontSize: 'inherit',
                              }}
                            >
                              {counts.unread}
                            </Typography>
                            )
                          </Box>
                        ) : (
                          `${readStatusLabels[status]} (${
                            status === 'all'
                              ? counts.total
                              : counts[status] || 0
                          })`
                        )
                      }
                      color={
                        activeReadStatus === status ? 'primary' : 'default'
                      }
                      variant={
                        activeReadStatus === status ? 'filled' : 'outlined'
                      }
                      onClick={() => {
                        handleReadStatusChangeWithCallback(status);
                      }}
                      clickable
                      sx={{
                        fontWeight: activeReadStatus === status ? 700 : 400,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
        }
        action={
          <IconButton
            data-tour="filters-expand-button"
            onClick={onToggleFiltersExpanded}
            sx={{
              transform: filtersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
        sx={{ pb: 1 }}
      />
      <Collapse in={filtersExpanded}>
        <CardContent sx={{ pt: 0, pl: 17 }}>
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
              data-tour="source-filters"
            >
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {filterSources.map((source) => {
                  const counts =
                    source === 'all'
                      ? readCounts
                      : sourceReadCounts[source] || {
                          total: 0,
                          read: 0,
                          unread: 0,
                        };

                  return (
                    <Chip
                      key={source}
                      icon={sourceIcons[source]}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {sourceLabels[source]} (
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: counts.unread > 0 ? 700 : 400,
                              fontSize: 'inherit',
                            }}
                          >
                            {counts.unread}
                          </Typography>
                          /{counts.total})
                        </Box>
                      }
                      color={activeSource === source ? 'primary' : 'default'}
                      variant={activeSource === source ? 'filled' : 'outlined'}
                      onClick={() => {
                        handleSourceChangeWithCallback(source);
                      }}
                      clickable
                      sx={{
                        fontWeight: activeSource === source ? 700 : 400,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
              data-tour="event-type-filters"
            >
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {filterTypes.map((type) => {
                  const counts =
                    type === 'all'
                      ? readCounts
                      : typeReadCounts[type] || {
                          total: 0,
                          read: 0,
                          unread: 0,
                        };

                  return (
                    <Chip
                      key={type}
                      icon={typeIcons[type]}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {typeLabels[type]} (
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: counts.unread > 0 ? 700 : 400,
                              fontSize: 'inherit',
                            }}
                          >
                            {counts.unread}
                          </Typography>
                          /{counts.total})
                        </Box>
                      }
                      color={activeType === type ? 'primary' : 'default'}
                      variant={activeType === type ? 'filled' : 'outlined'}
                      onClick={() => {
                        handleTypeChangeWithCallback(type);
                      }}
                      clickable
                      sx={{
                        fontWeight: activeType === type ? 700 : 400,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
              data-tour="notification-type-filters"
            >
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {filterNotificationTypes.map((notificationType) => {
                  const counts =
                    notificationType === 'all'
                      ? readCounts
                      : notificationTypeCounts[notificationType] || {
                          total: 0,
                          read: 0,
                          unread: 0,
                        };

                  return (
                    <Chip
                      key={notificationType}
                      icon={notificationTypeIcons[notificationType]}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {notificationTypeLabels[notificationType]} (
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: counts.unread > 0 ? 700 : 400,
                              fontSize: 'inherit',
                            }}
                          >
                            {counts.unread}
                          </Typography>
                          /{counts.total})
                        </Box>
                      }
                      color={
                        activeNotificationType === notificationType
                          ? 'primary'
                          : 'default'
                      }
                      variant={
                        activeNotificationType === notificationType
                          ? 'filled'
                          : 'outlined'
                      }
                      onClick={() => {
                        handleNotificationTypeChangeWithCallback(
                          notificationType
                        );
                      }}
                      clickable
                      sx={{
                        fontWeight:
                          activeNotificationType === notificationType
                            ? 700
                            : 400,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
              data-tour="ai-notification-filters"
            >
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {filterAITypes.map((type) => {
                  const counts = typeReadCounts[type] || {
                    total: 0,
                    read: 0,
                    unread: 0,
                  };

                  return (
                    <Chip
                      key={type}
                      icon={aiTypeIcons[type]}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {aiTypeLabels[type]} (
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: counts.unread > 0 ? 700 : 400,
                              fontSize: 'inherit',
                            }}
                          >
                            {counts.unread}
                          </Typography>
                          /{counts.total})
                        </Box>
                      }
                      color={activeType === type ? 'primary' : 'default'}
                      variant={activeType === type ? 'filled' : 'outlined'}
                      onClick={() => {
                        handleTypeChangeWithCallback(type);
                      }}
                      clickable
                      sx={{
                        fontWeight: activeType === type ? 700 : 400,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Collapse>
      <Box sx={{ px: 2, pb: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          Displaying notifications from the last 30 days (maximum 1000)
        </Typography>
      </Box>
    </Card>
  );
};
